import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Box, Button, Container } from '@mui/material';

import CardWithShadow from '@components/CardWithShadow/CardWithShadow';
import urls from '@routes/urls';
import { authTokenSelector } from '@store/features/auth/token/selectors';

import './style.scss';

const handleAuthActions = (isLogin: boolean): void => {
  if (process.env.REACT_APP_LOGIN_URL && isLogin) {
    window.location.href = process.env.REACT_APP_LOGIN_URL;
  } else if (process.env.REACT_APP_SIGNUP_URL && !isLogin) {
    window.location.href = process.env.REACT_APP_SIGNUP_URL;
  }
};

const LoginPage: FC = (): JSX.Element => {
  const { t } = useTranslation();

  const authToken = useSelector(authTokenSelector);

  if (authToken) {
    return <Navigate to={urls.home} replace />;
  }

  return (
    <Container maxWidth="xl" className="login">
      <Box className="login__logo">
        <Box
          component="img"
          sx={{
            width: { xs: 175, lg: 200 },
          }}
          alt={t('common.axisAltText')}
          src="/images/logo.svg"
        />
      </Box>

      <CardWithShadow className="login__content">
        <Box className="login__content__description">{t('common.auth.description')}</Box>
        <Button
          variant="contained"
          className="login__button"
          onClick={() => handleAuthActions(true)}
        >
          {t('common.auth.login')}
        </Button>
      </CardWithShadow>

      <Box className="login__signup">
        <Box className="login__signup-title">{t('common.auth.dontHaveAnAccount')}</Box>

        <Button
          variant="outlined"
          className="login__button"
          onClick={() => handleAuthActions(false)}
        >
          {t('common.auth.signUp')}
        </Button>
      </Box>

      <Box className="login__links">
        <div className="login__links-title">{t('common.auth.needHelp')}</div>

        <ul className="login__links-items" role="navigation">
          <li className="login__links-item">
            <a href={process.env.REACT_APP_AXIS_USER_GUIDE || ''} target="_blank" rel="noreferrer">
              {t('common.auth.userGuide')}
            </a>
          </li>
          <li className="login__links-item">
            <a href={process.env.REACT_APP_AXIS_HELP_VIDEO || ''} target="_blank" rel="noreferrer">
              {t('common.auth.helpVideo')}
            </a>
          </li>
        </ul>
      </Box>
    </Container>
  );
};

export default LoginPage;
